import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { useNls } from '../../../nls/nlsProvider'
import { MobileSubtabBar } from '../../Common/MobileSubtabBar'
import type { MobileTabItem } from '../../Common/MobileTabs'
import { RollCallRow, type RollCallStatus } from '../../Common/RollCallRow'
import { RollSummaryBar, type RollSummaryCounts } from '../../Common/RollSummaryBar'
import { Button, Pressable, Select, TextInput } from '../../Common'
import {
  getHelperEduJobs,
  getHelperSnapshot,
  subscribeHelperStore,
  type EduHelperJob,
} from '../../../services/helper/helperStore'
import {
  getEduSessionRoll,
  markEduAttendance,
  type GetEduSessionRollResult,
} from '../../../services/eduAttendanceService'
import {
  listGateLearners,
  recordPickup,
  openRouteRun,
  closeRouteRun,
  sendRouteLateNotice,
  logVisitor,
  checkOutVisitor,
  logWalkInEnquiry,
  queueEduAttendanceMark,
  queueEduBoarding,
  type GateLearner,
  type GateLearnerGuardian,
  type OpenRouteRunResult,
} from '../../../services/eduHelperJobsService'

export type SchoolSubtabKey = EduHelperJob

export interface SchoolRouteStop {
  stopId: string
  stopName: string
  order: number
  learners: Array<{ learnerId: string; displayName: string }>
}

export interface SchoolTabProps {
  workspaceId: string
  workspaceName?: string
  initialSubtab?: SchoolSubtabKey
  className?: string
}

function createClientMutationId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
}

export const SchoolTab = ({
  workspaceId,
  initialSubtab,
  className,
}: SchoolTabProps) => {
  const { t } = useNls()
  const [snapshot, setSnapshot] = useState(getHelperSnapshot)
  useEffect(() => subscribeHelperStore(setSnapshot), [])

  const assignedJobs = useMemo<EduHelperJob[]>(() => {
    const list = snapshot.eduJobsByWorkspace[workspaceId] ?? (snapshot.activeWorkspaceId === workspaceId ? snapshot.eduJobs : [])
    return list && list.length > 0 ? list : getHelperEduJobs(workspaceId)
  }, [snapshot, workspaceId])

  // Order: ROLL, GATE, BUS, DESK
  const visibleSubtabs = useMemo<EduHelperJob[]>(() => {
    const order: EduHelperJob[] = ['ROLL', 'GATE', 'BUS', 'DESK']
    return order.filter((job) => assignedJobs.includes(job))
  }, [assignedJobs])

  const [activeSubtabState, setActiveSubtab] = useState<EduHelperJob>(() => {
    if (initialSubtab && visibleSubtabs.includes(initialSubtab)) return initialSubtab
    return visibleSubtabs[0] ?? 'ROLL'
  })

  const activeSubtab = visibleSubtabs.includes(activeSubtabState)
    ? activeSubtabState
    : (visibleSubtabs[0] ?? 'ROLL')

  const subtabItems = useMemo<Array<MobileTabItem<EduHelperJob>>>(() => {
    const labels: Record<EduHelperJob, string> = {
      ROLL: t('NLSHelperSchoolSubtabRoll') || 'Roll',
      GATE: t('NLSHelperSchoolSubtabGate') || 'Gate',
      BUS: t('NLSHelperSchoolSubtabBus') || 'Bus',
      DESK: t('NLSHelperSchoolSubtabDesk') || 'Desk',
    }
    return visibleSubtabs.map((key) => ({
      key,
      label: labels[key],
      testId: `helper-school-subtab-${key.toLowerCase()}`,
    }))
  }, [visibleSubtabs, t])

  // --- Subtab 1: ROLL State ---
  const rollSessionIds = snapshot.activeWorkspaceId === workspaceId && snapshot.eduRollSessionIds.length > 0
    ? snapshot.eduRollSessionIds
    : ['series-1']
  const [selectedSeriesId, setSelectedSeriesId] = useState<string>(() => rollSessionIds[0] ?? 'series-1')
  const [loadedSeriesId, setLoadedSeriesId] = useState<string | null>(null)
  const [rollFeedback, setRollFeedback] = useState<string | null>(null)
  const rollLoading = Boolean(selectedSeriesId && loadedSeriesId !== selectedSeriesId && !rollFeedback)
  const [rollLearners, setRollLearners] = useState<Array<{ learnerId: string; displayName: string; rollNo: string | null }>>([])
  const [rollMarks, setRollMarks] = useState<Record<string, RollCallStatus>>({})
  const [rollSubmitting, setRollSubmitting] = useState(false)

  const todayIso = useMemo(() => new Date().toISOString().split('T')[0], [])

  useEffect(() => {
    if (activeSubtab !== 'ROLL' || !selectedSeriesId) return
    let active = true
    getEduSessionRoll({
      workspaceId,
      seriesId: selectedSeriesId,
      date: todayIso,
    })
      .then((res: GetEduSessionRollResult) => {
        if (!active) return
        setRollLearners(res.learners.map((l) => ({
          learnerId: l.learnerId,
          displayName: l.displayName,
          rollNo: l.rollNo,
        })))
        const initialMarks: Record<string, RollCallStatus> = {}
        for (const l of res.learners) {
          const raw = res.marks[l.learnerId]
          if (raw === 'ON_LEAVE') initialMarks[l.learnerId] = 'OL'
          else if (raw === 'ABSENT') initialMarks[l.learnerId] = 'A'
          else if (raw === 'LATE') initialMarks[l.learnerId] = 'L'
          else initialMarks[l.learnerId] = 'P'
        }
        setRollMarks(initialMarks)
        setRollFeedback(null)
        setLoadedSeriesId(selectedSeriesId)
      })
      .catch((err) => {
        if (!active) return
        setRollFeedback(err instanceof Error ? err.message : (t('NLSHelperSchoolRollLoadError') || 'Could not load roll'))
        setLoadedSeriesId(selectedSeriesId)
      })
    return () => {
      active = false
    }
  }, [activeSubtab, workspaceId, selectedSeriesId, todayIso, t])

  const handleRollMarkChange = (learnerId: string, nextStatus: RollCallStatus) => {
    setRollMarks((prev) => ({
      ...prev,
      [learnerId]: nextStatus,
    }))
  }

  const rollCounts = useMemo<RollSummaryCounts>(() => {
    let present = 0
    let absent = 0
    let late = 0
    let onLeave = 0
    for (const status of Object.values(rollMarks)) {
      if (status === 'P') present++
      else if (status === 'A') absent++
      else if (status === 'L') late++
      else if (status === 'OL') onLeave++
    }
    return {
      present,
      absent,
      late,
      onLeave,
      total: rollLearners.length,
    }
  }, [rollMarks, rollLearners])

  const submitRoll = async () => {
    if (!selectedSeriesId || rollSubmitting) return
    setRollSubmitting(true)
    setRollFeedback(null)
    const marksPayload = Object.entries(rollMarks).map(([learnerId, status]) => {
      const mapped = status === 'P' ? 'PRESENT' : status === 'A' ? 'ABSENT' : status === 'L' ? 'LATE' : 'ON_LEAVE'
      return { learnerId, status: mapped as 'PRESENT' | 'ABSENT' | 'LATE' | 'ON_LEAVE' }
    })
    const isOnline = typeof navigator === 'undefined' ? true : navigator.onLine
    try {
      if (isOnline) {
        await markEduAttendance({
          workspaceId,
          seriesId: selectedSeriesId,
          date: todayIso,
          marks: marksPayload,
        })
        setRollFeedback(t('NLSHelperSchoolRollSubmitted') || 'Roll submitted successfully')
      } else {
        await queueEduAttendanceMark({
          workspaceId,
          seriesId: selectedSeriesId,
          date: todayIso,
          marks: marksPayload,
          clientMutationId: `roll-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        })
        setRollFeedback(t('NLSHelperSchoolRollQueuedOffline') || 'Roll queued offline — will sync when connected')
      }
    } catch (caught) {
      setRollFeedback(caught instanceof Error ? caught.message : (t('NLSHelperSchoolRollSubmitError') || 'Failed to submit roll'))
    } finally {
      setRollSubmitting(false)
    }
  }

  // --- Subtab 2: GATE State ---
  const [gateSearch, setGateSearch] = useState('')
  const [gateLearners, setGateLearners] = useState<GateLearner[]>([])
  const [gateSelectedLearner, setGateSelectedLearner] = useState<GateLearner | null>(null)
  const [gateOneOffName, setGateOneOffName] = useState('')
  const [gateOneOffRelation, setGateOneOffRelation] = useState('')
  const [gateOfficeRequired, setGateOfficeRequired] = useState<string | null>(null)
  const [gateFeedback, setGateFeedback] = useState<string | null>(null)
  const [gateBusy, setGateBusy] = useState(false)
  const isOnline = typeof navigator === 'undefined' ? true : navigator.onLine

  useEffect(() => {
    if (activeSubtab !== 'GATE') return
    let active = true
    listGateLearners({ workspaceId })
      .then((res) => {
        if (active) setGateLearners(res.learners ?? [])
      })
      .catch(() => {
        if (active) setGateLearners([])
      })
    return () => {
      active = false
    }
  }, [activeSubtab, workspaceId])

  const filteredGateLearners = useMemo(() => {
    if (!gateSearch.trim()) return gateLearners
    const s = gateSearch.toLowerCase()
    return gateLearners.filter(
      (l) => l.displayName.toLowerCase().includes(s) || l.classOrBatch.toLowerCase().includes(s),
    )
  }, [gateLearners, gateSearch])

  const handleRecordPickup = async (guardian?: GateLearnerGuardian, oneOffName?: string) => {
    if (!gateSelectedLearner) return
    const nameToUse = guardian?.displayName ?? oneOffName?.trim()
    if (!nameToUse) return
    setGateBusy(true)
    setGateFeedback(null)
    setGateOfficeRequired(null)
    try {
      await recordPickup({
        workspaceId,
        learnerId: gateSelectedLearner.learnerId,
        collector: guardian ? { guardianUid: guardian.uid, name: guardian.displayName } : { name: nameToUse },
      })
      setGateFeedback(t('NLSHelperSchoolGatePickupRecorded') || 'Pickup recorded')
      setGateOneOffName('')
      setGateOneOffRelation('')
    } catch (caught: unknown) {
      const msg = caught instanceof Error ? caught.message : String(caught)
      if (msg.includes('Office must approve') || msg.toLowerCase().includes('approval')) {
        setGateOfficeRequired(msg)
      } else {
        setGateFeedback(msg)
      }
    } finally {
      setGateBusy(false)
    }
  }

  // --- Subtab 3: BUS State ---
  const routeIds = snapshot.activeWorkspaceId === workspaceId && snapshot.eduRouteIds.length > 0
    ? snapshot.eduRouteIds
    : ['route-1']
  const [busRouteId, setBusRouteId] = useState<string>(() => routeIds[0] ?? 'route-1')
  const [busLeg, setBusLeg] = useState<'AM' | 'PM'>('AM')
  const [busRun, setBusRun] = useState<OpenRouteRunResult | null>(null)
  const [busStops, setBusStops] = useState<SchoolRouteStop[]>([])
  const [busBusy, setBusBusy] = useState(false)
  const [busLateSent, setBusLateSent] = useState(false)
  const [busCloseConfirmOpen, setBusCloseConfirmOpen] = useState(false)
  const [busBoardingStatus, setBusBoardingStatus] = useState<Record<string, 'BOARDED' | 'NOT_HERE'>>({})

  const handleOpenRouteRun = async () => {
    setBusBusy(true)
    try {
      const res = await openRouteRun({
        workspaceId,
        routeId: busRouteId,
        date: todayIso,
        leg: busLeg,
      })
      setBusRun(res)
      setBusLateSent(false)
      const defaultStops: SchoolRouteStop[] = [
        {
          stopId: 'stop-1',
          stopName: 'Stop 1 - North Gate',
          order: 1,
          learners: [
            { learnerId: 'learner-1', displayName: 'Student One' },
            { learnerId: 'learner-2', displayName: 'Student Two' },
          ],
        },
      ]
      setBusStops(defaultStops)
      setBusBoardingStatus({})
    } catch {
      // ignore
    } finally {
      setBusBusy(false)
    }
  }

  const handleToggleBoarding = async (learnerId: string, status: 'BOARDED' | 'NOT_HERE') => {
    if (!busRun) return
    setBusBoardingStatus((prev) => ({ ...prev, [learnerId]: status }))
    await queueEduBoarding({
      workspaceId,
      runId: busRun.runId,
      learnerId,
      status,
      clientMutationId: createClientMutationId('boarding'),
    })
  }

  const handleSendLateNotice = async () => {
    if (!busRun) return
    try {
      await sendRouteLateNotice({
        workspaceId,
        runId: busRun.runId,
        delayMinutes: 10,
        message: 'Delay reported by driver',
      })
      setBusLateSent(true)
    } catch {
      // ignore
    }
  }

  const notBoardedCount = useMemo(() => {
    if (!busStops) return 0
    let count = 0
    for (const stop of busStops) {
      for (const l of stop.learners) {
        if (busBoardingStatus[l.learnerId] !== 'BOARDED') count++
      }
    }
    return count
  }, [busStops, busBoardingStatus])

  const handleCloseRunConfirm = async () => {
    if (!busRun) return
    setBusBusy(true)
    try {
      await closeRouteRun({
        workspaceId,
        runId: busRun.runId,
      })
      setBusRun(null)
      setBusCloseConfirmOpen(false)
    } catch {
      // ignore
    } finally {
      setBusBusy(false)
    }
  }

  // --- Subtab 4: DESK State ---
  const [visitorName, setVisitorName] = useState('')
  const [visitorReason, setVisitorReason] = useState('')
  const [visitorMeetingWhom, setVisitorMeetingWhom] = useState('')
  const [visitorPassNo, setVisitorPassNo] = useState('')
  const [activeVisitors, setActiveVisitors] = useState<Array<{ id: string; name: string; passNo: string; checkInAt: string }>>([])
  const [walkInName, setWalkInName] = useState('')
  const [walkInPhone, setWalkInPhone] = useState('')
  const [walkInInterest, setWalkInInterest] = useState('')
  const [walkInNotes, setWalkInNotes] = useState('')
  const [deskFeedback, setDeskFeedback] = useState<string | null>(null)

  const handleLogVisitor = async (e: FormEvent) => {
    e.preventDefault()
    if (!visitorName.trim()) return
    try {
      const res = await logVisitor({
        workspaceId,
        name: visitorName.trim(),
        reason: visitorReason.trim() || 'Visit',
        meetingWhom: visitorMeetingWhom.trim() || 'Administration',
        passNo: visitorPassNo.trim() || `P-${Date.now().toString().slice(-4)}`,
      })
      setActiveVisitors((prev) => [
        {
          id: res.visitorId,
          name: visitorName.trim(),
          passNo: visitorPassNo.trim() || `P-${Date.now().toString().slice(-4)}`,
          checkInAt: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        },
        ...prev,
      ])
      setVisitorName('')
      setVisitorReason('')
      setVisitorMeetingWhom('')
      setVisitorPassNo('')
      setDeskFeedback(t('NLSHelperSchoolDeskVisitorCheckedIn') || 'Visitor checked in')
    } catch (caught) {
      setDeskFeedback(caught instanceof Error ? caught.message : (t('NLSHelperSchoolDeskLogVisitorError') || 'Could not log visitor'))
    }
  }

  const handleCheckOut = async (visitorId: string) => {
    try {
      await checkOutVisitor({
        workspaceId,
        visitorId,
      })
      setActiveVisitors((prev) => prev.filter((v) => v.id !== visitorId))
      setDeskFeedback(t('NLSHelperSchoolDeskCheckedOut') || 'Checked out')
    } catch {
      // ignore
    }
  }

  const handleLogWalkIn = async (e: FormEvent) => {
    e.preventDefault()
    if (!walkInName.trim()) return
    try {
      await logWalkInEnquiry({
        workspaceId,
        contact: {
          name: walkInName.trim(),
          phone: walkInPhone.trim(),
        },
        learner: {
          name: walkInName.trim(),
          isMinor: false,
        },
        interestUseCase: 'TEACHING',
        reason: walkInInterest.trim() || undefined,
      })
      setWalkInName('')
      setWalkInPhone('')
      setWalkInInterest('')
      setWalkInNotes('')
      setDeskFeedback(t('NLSHelperSchoolDeskEnquiryLogged') || 'Enquiry logged successfully')
    } catch (caught) {
      setDeskFeedback(caught instanceof Error ? caught.message : (t('NLSHelperSchoolDeskLogEnquiryError') || 'Could not log enquiry'))
    }
  }

  return (
    <div
      className={['flex-1 flex flex-col min-h-0 bg-[var(--surface)] text-[var(--text)]', className]
        .filter(Boolean)
        .join(' ')}
      data-testid="helper-school-tab"
    >
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {/* Subtab 1: ROLL */}
        {activeSubtab === 'ROLL' ? (
          <section className="space-y-4" data-testid="school-roll-section">
            <header className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold tracking-tight text-[var(--text-strong)]">
                  {t('NLSHelperSchoolRollTitle') || "Today's Roll"}
                </h2>
                <p className="text-xs text-[var(--muted)]">{todayIso}</p>
              </div>
              {rollSessionIds.length > 1 ? (
                <Select
                  size="sm"
                  value={selectedSeriesId}
                  onChange={(val) => setSelectedSeriesId(String(val))}
                  options={rollSessionIds.map((id) => ({ value: id, label: id }))}
                  ariaLabel={t('NLSHelperSchoolRollSelectSession') || 'Select session'}
                />
              ) : null}
            </header>

            {rollFeedback ? (
              <div
                className="rounded-xl border border-[var(--border-subtle)] bg-[var(--surface-card)] p-3 text-xs font-medium"
                data-testid="school-roll-feedback"
              >
                {rollFeedback}
              </div>
            ) : null}

            {rollLoading ? (
              <div className="py-8 text-center text-sm text-[var(--muted)]">
                {t('NLSHelperSchoolRollLoading') || 'Loading roll...'}
              </div>
            ) : rollLearners.length === 0 ? (
              <div className="rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface-card)] p-6 text-center text-sm text-[var(--muted)]">
                {t('NLSHelperSchoolRollEmpty') || 'No roll sessions assigned for today.'}
              </div>
            ) : (
              <div className="space-y-2">
                <div
                  className="rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface-card)] divide-y divide-[var(--border-subtle)] overflow-hidden"
                  data-testid="school-roll-list"
                >
                  {rollLearners.map((learner) => (
                    <RollCallRow
                      key={learner.learnerId}
                      rollNumber={learner.rollNo ?? undefined}
                      name={learner.displayName}
                      status={rollMarks[learner.learnerId] ?? 'P'}
                      locked={rollMarks[learner.learnerId] === 'OL'}
                      onChange={(next) => handleRollMarkChange(learner.learnerId, next)}
                      testId={`roll-row-${learner.learnerId}`}
                    />
                  ))}
                </div>

                <RollSummaryBar
                  counts={rollCounts}
                  actionLabel={
                    rollSubmitting
                      ? t('NLSHelperSchoolRollSubmitting') || 'Submitting...'
                      : t('NLSHelperSchoolRollSubmit') || 'Submit roll'
                  }
                  onAction={submitRoll}
                  busy={rollSubmitting}
                  testId="school-roll-summary"
                />
              </div>
            )}
          </section>
        ) : null}

        {/* Subtab 2: GATE */}
        {activeSubtab === 'GATE' ? (
          <section className="space-y-4" data-testid="school-gate-section">
            <header>
              <h2 className="text-lg font-semibold tracking-tight text-[var(--text-strong)]">
                {t('NLSHelperSchoolGateTitle') || 'Gate Pickups'}
              </h2>
            </header>

            {!isOnline ? (
              <div
                className="rounded-xl border border-[color-mix(in_srgb,var(--warning)_30%,transparent)] bg-[color-mix(in_srgb,var(--warning)_10%,transparent)] p-3 text-xs text-[color-mix(in_srgb,var(--warning)_80%,var(--text-strong))]"
                data-testid="school-gate-offline-warning"
              >
                {t('NLSHelperSchoolGateOfflineWarning') || 'Gate pickups require an active connection for safety checks.'}
              </div>
            ) : null}

            {gateFeedback ? (
              <div className="rounded-xl border border-[var(--border-subtle)] bg-[var(--surface-card)] p-3 text-xs font-medium">
                {gateFeedback}
              </div>
            ) : null}

            {gateOfficeRequired ? (
              <div
                className="rounded-2xl border border-[color-mix(in_srgb,var(--danger)_30%,transparent)] bg-[color-mix(in_srgb,var(--danger)_10%,transparent)] p-4 space-y-2 text-xs text-[color-mix(in_srgb,var(--danger)_80%,var(--text-strong))]"
                data-testid="school-gate-office-required"
              >
                <p className="font-semibold">{t('NLSHelperSchoolGateOfficeApprovalRequired') || 'Office must approve this pickup before departure.'}</p>
                <p>{gateOfficeRequired}</p>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleRecordPickup(undefined, gateOneOffName)}
                  disabled={Boolean(gateBusy || !isOnline)}
                  data-testid="school-gate-retry-btn"
                >
                  {t('NLSHelperSchoolGateRetryApproval') || 'Retry approval check'}
                </Button>
              </div>
            ) : null}

            <TextInput
              value={gateSearch}
              onChange={setGateSearch}
              placeholder={t('NLSHelperSchoolGateSearchPlaceholder') || 'Search learner by name or roll no...'}
              data-testid="school-gate-search-input"
            />

            {!gateSelectedLearner ? (
              <div className="space-y-2">
                <p className="text-xs font-medium text-[var(--muted)]">
                  {t('NLSHelperSchoolGateLearnerSelect') || 'Select learner'}
                </p>
                <div className="rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface-card)] divide-y divide-[var(--border-subtle)] overflow-hidden">
                  {filteredGateLearners.map((learner) => (
                    <Pressable
                      key={learner.learnerId}
                      onClick={() => {
                        setGateSelectedLearner(learner)
                        setGateOfficeRequired(null)
                        setGateFeedback(null)
                      }}
                      className="w-full flex items-center justify-between p-3 text-left hover:bg-[var(--surface-hover)] min-h-[44px]"
                      data-testid={`gate-learner-${learner.learnerId}`}
                    >
                      <div>
                        <p className="text-sm font-semibold">{learner.displayName}</p>
                        <p className="text-xs text-[var(--muted)]">
                          {learner.classOrBatch ? (t('NLSHelperSchoolGateClass', { class: learner.classOrBatch }) || `Class: ${learner.classOrBatch}`) : ''}
                        </p>
                      </div>
                      <span className="text-xs font-medium text-[var(--accent-strong)]">
                        {t('NLSHelperSchoolGateSelectArrow') || 'Select →'}
                      </span>
                    </Pressable>
                  ))}
                  {filteredGateLearners.length === 0 ? (
                    <div className="p-4 text-center text-xs text-[var(--muted)]">
                      {t('NLSHelperSchoolGateNoLearnersFound') || 'No learners found'}
                    </div>
                  ) : null}
                </div>
              </div>
            ) : (
              <div className="space-y-4 rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface-card)] p-4">
                <div className="flex items-center justify-between border-b border-[var(--border-subtle)] pb-3">
                  <div>
                    <h3 className="text-base font-semibold">{gateSelectedLearner.displayName}</h3>
                    <p className="text-xs text-[var(--muted)]">
                      {gateSelectedLearner.classOrBatch ? (t('NLSHelperSchoolGateClass', { class: gateSelectedLearner.classOrBatch }) || `Class: ${gateSelectedLearner.classOrBatch}`) : ''}
                    </p>
                  </div>
                  <Button
                    variant="tertiary"
                    size="sm"
                    onClick={() => {
                      setGateSelectedLearner(null)
                      setGateOfficeRequired(null)
                    }}
                  >
                    {t('NLSHelperSchoolGateChangeLearner') || 'Change'}
                  </Button>
                </div>

                <div className="space-y-2">
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-[var(--muted)]">
                    {t('NLSHelperSchoolGateCollectorsTitle') || 'Authorized collectors on file'}
                  </h4>
                  {gateSelectedLearner.guardians && gateSelectedLearner.guardians.length > 0 ? (
                    <div className="space-y-2">
                      {gateSelectedLearner.guardians.map((c: GateLearnerGuardian) => (
                        <div
                          key={c.uid}
                          className="flex items-center justify-between rounded-xl border border-[var(--border-subtle)] p-3 min-h-[44px]"
                          data-testid={`gate-collector-${c.uid}`}
                        >
                          <div>
                            <p className="text-sm font-medium">{c.displayName}</p>
                            <p className="text-xs text-[var(--muted)]">{c.relation}</p>
                          </div>
                          <Button
                            variant="primary"
                            size="sm"
                            disabled={Boolean(gateBusy || !isOnline)}
                            onClick={() => handleRecordPickup(c)}
                            data-testid={`record-pickup-${c.uid}`}
                          >
                            {t('NLSHelperSchoolGateRecordPickup') || 'Record pickup'}
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-[var(--muted)]">
                      {t('NLSHelperSchoolGateNoCollectors') || 'No authorized collectors on file.'}
                    </p>
                  )}
                </div>

                <div className="space-y-3 pt-3 border-t border-[var(--border-subtle)]">
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-[var(--muted)]">
                    {t('NLSHelperSchoolGateOneOffTitle') || 'Or enter one-off collector'}
                  </h4>
                  <TextInput
                    value={gateOneOffName}
                    onChange={setGateOneOffName}
                    placeholder={t('NLSHelperSchoolGateOneOffName') || 'Collector full name'}
                    data-testid="gate-one-off-name-input"
                  />
                  <TextInput
                    value={gateOneOffRelation}
                    onChange={setGateOneOffRelation}
                    placeholder={t('NLSHelperSchoolGateOneOffRelation') || 'Relation to learner'}
                    data-testid="gate-one-off-relation-input"
                  />
                  <Button
                    variant="secondary"
                    className="w-full"
                    disabled={Boolean(gateBusy || !gateOneOffName.trim() || !isOnline)}
                    onClick={() => handleRecordPickup(undefined, gateOneOffName)}
                    data-testid="gate-one-off-submit-btn"
                  >
                    {t('NLSHelperSchoolGateRecordPickup') || 'Record pickup'}
                  </Button>
                </div>
              </div>
            )}
          </section>
        ) : null}

        {/* Subtab 3: BUS */}
        {activeSubtab === 'BUS' ? (
          <section className="space-y-4" data-testid="school-bus-section">
            <header>
              <h2 className="text-lg font-semibold tracking-tight text-[var(--text-strong)]">
                {t('NLSHelperSchoolBusTitle') || 'Transport Routes'}
              </h2>
            </header>

            {!busRun ? (
              <div className="rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface-card)] p-4 space-y-4">
                <div className="space-y-2">
                  <label className="text-xs font-medium text-[var(--muted)]">
                    {t('NLSHelperSchoolBusSelectRoute') || 'Select route'}
                  </label>
                  <Select
                    size="md"
                    value={busRouteId}
                    onChange={(val) => setBusRouteId(String(val))}
                    options={routeIds.map((id) => ({ value: id, label: id }))}
                    ariaLabel={t('NLSHelperSchoolBusSelectRoute') || 'Select route'}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-medium text-[var(--muted)]">
                    {t('NLSHelperSchoolBusSelectLeg') || 'Leg'}
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <Pressable
                      onClick={() => setBusLeg('AM')}
                      className={`h-11 rounded-xl border text-sm font-medium ${
                        busLeg === 'AM'
                          ? 'border-[var(--accent)] bg-[var(--accent-soft)] text-[var(--accent-strong)]'
                          : 'border-[var(--border)] bg-[var(--surface)]'
                      }`}
                      data-testid="school-bus-leg-am"
                    >
                      {t('NLSHelperSchoolBusLegMorning') || 'Morning (AM)'}
                    </Pressable>
                    <Pressable
                      onClick={() => setBusLeg('PM')}
                      className={`h-11 rounded-xl border text-sm font-medium ${
                        busLeg === 'PM'
                          ? 'border-[var(--accent)] bg-[var(--accent-soft)] text-[var(--accent-strong)]'
                          : 'border-[var(--border)] bg-[var(--surface)]'
                      }`}
                      data-testid="school-bus-leg-pm"
                    >
                      {t('NLSHelperSchoolBusLegAfternoon') || 'Afternoon (PM)'}
                    </Pressable>
                  </div>
                </div>

                <Button
                  variant="primary"
                  className="w-full"
                  disabled={busBusy}
                  onClick={handleOpenRouteRun}
                  data-testid="school-bus-open-run-btn"
                >
                  {busBusy
                    ? t('NLSHelperSchoolBusOpeningRun') || 'Opening run...'
                    : t('NLSHelperSchoolBusOpenRun') || 'Open route run'}
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface-card)] p-4">
                  <div>
                    <h3 className="font-semibold text-sm">
                      {busRouteId} ({busLeg})
                    </h3>
                    <p className="text-xs text-[var(--muted)]">
                      {t('NLSHelperSchoolBusRunId', { runId: busRun.runId }) || `Run ID: ${busRun.runId}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleSendLateNotice}
                      disabled={busLateSent}
                      data-testid="school-bus-late-btn"
                    >
                      {busLateSent
                        ? t('NLSHelperSchoolBusLateNoticeSent') || 'Late notice sent'
                        : t('NLSHelperSchoolBusRunningLate') || 'Running late'}
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => setBusCloseConfirmOpen(true)}
                      data-testid="school-bus-close-run-btn"
                    >
                      {t('NLSHelperSchoolBusCloseRun') || 'Close run'}
                    </Button>
                  </div>
                </div>

                {busCloseConfirmOpen ? (
                  <div
                    className="rounded-2xl border border-[color-mix(in_srgb,var(--warning)_30%,transparent)] bg-[color-mix(in_srgb,var(--warning)_10%,transparent)] p-4 space-y-3"
                    data-testid="school-bus-close-modal"
                  >
                    <h4 className="font-semibold text-sm text-[color-mix(in_srgb,var(--warning)_90%,var(--text-strong))]">
                      {t('NLSHelperSchoolBusCloseConfirmTitle') || 'Close Route Run'}
                    </h4>
                    <p className="text-xs text-[color-mix(in_srgb,var(--warning)_80%,var(--text-strong))]">
                      {t('NLSHelperSchoolBusCloseConfirmBody', { count: notBoardedCount }) ||
                        `${notBoardedCount} learners are not marked boarded. Close this run now?`}
                    </p>
                    <div className="flex gap-2 justify-end">
                      <Button
                        variant="tertiary"
                        size="sm"
                        onClick={() => setBusCloseConfirmOpen(false)}
                        data-testid="school-bus-cancel-close-btn"
                      >
                        {t('NLSHelperSchoolBusCancel') || 'Cancel'}
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        disabled={busBusy}
                        onClick={handleCloseRunConfirm}
                        data-testid="school-bus-confirm-close-btn"
                      >
                        {t('NLSHelperSchoolBusConfirmClose') || 'Confirm & close'}
                      </Button>
                    </div>
                  </div>
                ) : null}

                <div className="space-y-4" data-testid="school-bus-stops-list">
                  {busStops.map((stop) => (
                    <div
                      key={stop.stopId}
                      className="rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface-card)] p-4 space-y-3"
                    >
                      <div className="flex items-center justify-between border-b border-[var(--border-subtle)] pb-2">
                        <span className="font-semibold text-sm">{stop.stopName}</span>
                        <span className="text-xs text-[var(--muted)]">
                          {t('NLSHelperSchoolBusStopOrder', { order: stop.order }) || `Order: ${stop.order}`}
                        </span>
                      </div>
                      <div className="divide-y divide-[var(--border-subtle)]">
                        {stop.learners.map((l) => {
                          const status = busBoardingStatus[l.learnerId]
                          return (
                            <div
                              key={l.learnerId}
                              className="flex items-center justify-between py-2 min-h-[44px]"
                              data-testid={`bus-learner-row-${l.learnerId}`}
                            >
                              <span className="text-sm font-medium">{l.displayName}</span>
                              <div className="flex items-center gap-2">
                                <Pressable
                                  type="button"
                                  onClick={() => handleToggleBoarding(l.learnerId, 'BOARDED')}
                                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${
                                    status === 'BOARDED'
                                      ? 'bg-[var(--accent)] text-[var(--accent-contrast)]'
                                      : 'border border-[var(--border)] bg-[var(--surface)] text-[var(--text)]'
                                  }`}
                                  data-testid={`bus-boarded-btn-${l.learnerId}`}
                                >
                                  {t('NLSHelperSchoolBusBoarded') || 'Boarded'}
                                </Pressable>
                                <Pressable
                                  type="button"
                                  onClick={() => handleToggleBoarding(l.learnerId, 'NOT_HERE')}
                                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${
                                    status === 'NOT_HERE'
                                      ? 'bg-[var(--danger)] text-[var(--accent-contrast)]'
                                      : 'border border-[var(--border)] bg-[var(--surface)] text-[var(--text)]'
                                  }`}
                                  data-testid={`bus-not-here-btn-${l.learnerId}`}
                                >
                                  {t('NLSHelperSchoolBusNotHere') || 'Not here'}
                                </Pressable>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>
        ) : null}

        {/* Subtab 4: DESK */}
        {activeSubtab === 'DESK' ? (
          <section className="space-y-4" data-testid="school-desk-section">
            <header>
              <h2 className="text-lg font-semibold tracking-tight text-[var(--text-strong)]">
                {t('NLSHelperSchoolDeskTitle') || 'Front Desk'}
              </h2>
            </header>

            {deskFeedback ? (
              <div className="rounded-xl border border-[var(--border-subtle)] bg-[var(--surface-card)] p-3 text-xs font-medium">
                {deskFeedback}
              </div>
            ) : null}

            {/* Visitor Check-in Form */}
            <form
              onSubmit={handleLogVisitor}
              className="rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface-card)] p-4 space-y-3"
              data-testid="school-desk-visitor-form"
            >
              <h3 className="text-sm font-semibold text-[var(--text-strong)]">
                {t('NLSHelperSchoolDeskLogVisitor') || 'Log Visitor'}
              </h3>
              <TextInput
                value={visitorName}
                onChange={setVisitorName}
                placeholder={t('NLSHelperSchoolDeskVisitorName') || 'Visitor name'}
                data-testid="desk-visitor-name"
              />
              <TextInput
                value={visitorReason}
                onChange={setVisitorReason}
                placeholder={t('NLSHelperSchoolDeskPurpose') || 'Purpose of visit'}
                data-testid="desk-visitor-purpose"
              />
              <TextInput
                value={visitorMeetingWhom}
                onChange={setVisitorMeetingWhom}
                placeholder={t('NLSHelperSchoolDeskMeetingWhom') || 'Meeting whom'}
                data-testid="desk-visitor-meeting"
              />
              <TextInput
                value={visitorPassNo}
                onChange={setVisitorPassNo}
                placeholder={t('NLSHelperSchoolDeskPassNo') || 'Badge / Pass #'}
                data-testid="desk-visitor-pass-no"
              />
              <Button variant="primary" type="submit" disabled={!visitorName.trim()} className="w-full">
                {t('NLSHelperSchoolDeskCheckIn') || 'Check in visitor'}
              </Button>
            </form>

            {/* Active Visitors List */}
            <div className="space-y-2">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-[var(--muted)]">
                {t('NLSHelperSchoolDeskActiveVisitors') || 'Active Visitors'}
              </h3>
              <div className="rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface-card)] divide-y divide-[var(--border-subtle)] overflow-hidden">
                {activeVisitors.map((v) => (
                  <div
                    key={v.id}
                    className="flex items-center justify-between p-3 min-h-[44px]"
                    data-testid={`active-visitor-${v.id}`}
                  >
                    <div>
                      <p className="text-sm font-medium">{v.name}</p>
                      <p className="text-xs text-[var(--muted)]">
                        {t('NLSHelperSchoolDeskPassMeta', { passNo: v.passNo, checkInAt: v.checkInAt }) || `Pass: ${v.passNo} · In: ${v.checkInAt}`}
                      </p>
                    </div>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleCheckOut(v.id)}
                      data-testid={`checkout-visitor-${v.id}`}
                    >
                      {t('NLSHelperSchoolDeskCheckOut') || 'Check out'}
                    </Button>
                  </div>
                ))}
                {activeVisitors.length === 0 ? (
                  <div className="p-4 text-center text-xs text-[var(--muted)]">
                    {t('NLSHelperSchoolDeskNoVisitors') || 'No active visitors currently checked in.'}
                  </div>
                ) : null}
              </div>
            </div>

            {/* Walk-in Enquiry */}
            <form
              onSubmit={handleLogWalkIn}
              className="rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface-card)] p-4 space-y-3"
              data-testid="school-desk-walkin-form"
            >
              <h3 className="text-sm font-semibold text-[var(--text-strong)]">
                {t('NLSHelperSchoolDeskWalkInTitle') || 'Walk-In Enquiry'}
              </h3>
              <TextInput
                value={walkInName}
                onChange={setWalkInName}
                placeholder={t('NLSHelperSchoolDeskVisitorName') || 'Visitor name'}
                data-testid="desk-walkin-name"
              />
              <TextInput
                value={walkInPhone}
                onChange={setWalkInPhone}
                placeholder={t('NLSHelperSchoolDeskPhone') || 'Contact phone'}
                data-testid="desk-walkin-phone"
              />
              <TextInput
                value={walkInInterest}
                onChange={setWalkInInterest}
                placeholder={t('NLSHelperSchoolDeskInterest') || 'Course or grade of interest'}
                data-testid="desk-walkin-interest"
              />
              <TextInput
                value={walkInNotes}
                onChange={setWalkInNotes}
                placeholder={t('NLSHelperSchoolDeskNotes') || 'Notes / remarks'}
                data-testid="desk-walkin-notes"
              />
              <Button variant="secondary" type="submit" disabled={!walkInName.trim()} className="w-full">
                {t('NLSHelperSchoolDeskSubmitEnquiry') || 'Log walk-in enquiry'}
              </Button>
            </form>
          </section>
        ) : null}
      </div>

      {/* Subtab Bar (EM-D20: bottom placement; hidden if only 1 job) */}
      {visibleSubtabs.length > 1 ? (
        <MobileSubtabBar
          items={subtabItems}
          activeKey={activeSubtab}
          onChange={setActiveSubtab}
          ariaLabel={t('NLSHelperSchoolSubtabsAria') || 'School sections'}
          testId="helper-school-subtabs"
        />
      ) : null}
    </div>
  )
}

export default SchoolTab
